import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionPlansAsyncThunk } from './subscriptionThunk';
import { clearError, setFilters, setCurrentPage, setItemsPerPage } from './subscriptionReducer';
import { useDebounce } from '../../utils/useDebounce';
import SearchInput from '../../components/SearchInput';
import Pagination from '../../components/Pagination';
import { openModal } from '../common/modalSlice';
import { MODAL_BODY_TYPES } from '../../utils/globalConstantUtil';

function SubscriptionPlans() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { subscriptionPlans, pagination, isLoading, filters } = useSelector((state) => state.subscription);
  
  const [localSearchTerm, setLocalSearchTerm] = useState(filters.search || '');
  const [shouldRestoreFocus, setShouldRestoreFocus] = useState(false);
  const debouncedSearchTerm = useDebounce(localSearchTerm, 500);
  const searchInputRef = useRef(null);

  // Sync local search term with Redux filters when filters change externally
  useEffect(() => {
    setLocalSearchTerm(filters.search || '');
  }, [filters.search]);

  // Effect to handle debounced search
  useEffect(() => {
    if (debouncedSearchTerm !== filters.search) {
      // Check if search input is currently focused
      const isSearchFocused = document.activeElement === searchInputRef.current;
      if (isSearchFocused) {
        setShouldRestoreFocus(true);
      }
      
      dispatch(setFilters({ search: debouncedSearchTerm }));
      dispatch(setCurrentPage(1)); // Reset to first page when searching
      dispatch(getSubscriptionPlansAsyncThunk({
        page: 1,
        limit: pagination.limit,
        search: debouncedSearchTerm,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      }));
    }
  }, [debouncedSearchTerm, dispatch, filters.search, pagination.limit]);

  // Restore focus after component re-renders due to API calls
  useEffect(() => {
    if (shouldRestoreFocus && searchInputRef.current && !isLoading) {
      setTimeout(() => {
        searchInputRef.current?.focus();
        setShouldRestoreFocus(false);
      }, 0);
    }
  }, [shouldRestoreFocus, isLoading]);

  // Restore focus after any Redux state change that causes re-render
  useEffect(() => {
    if (shouldRestoreFocus && searchInputRef.current) {
      requestAnimationFrame(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      });
    }
  }, [subscriptionPlans, pagination, shouldRestoreFocus]);

  // Fetch data when pagination changes
  useEffect(() => {
    dispatch(getSubscriptionPlansAsyncThunk({
      page: pagination.page,
      limit: pagination.limit,
      search: filters.search,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    }));
  }, [dispatch, pagination.page, pagination.limit]);

  // Initial load
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleSearchInputFocus = useCallback(() => {
    setShouldRestoreFocus(true);
  }, []);

  const handleSearchInputBlur = useCallback(() => {
    setShouldRestoreFocus(false);
  }, []);

  const handleSearch = useCallback((searchTerm) => {
    setLocalSearchTerm(searchTerm);
  }, []);

  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    dispatch(setCurrentPage(page));
  }, [dispatch]);

  const handlePageSizeChange = useCallback((itemsPerPage) => {
    dispatch(setItemsPerPage(itemsPerPage));
  }, [dispatch]);

  // Handle view details
  const handleViewDetails = useCallback((plan) => {
    navigate(`/app/subscription-plans/${plan._id}`);
  }, [navigate]);

  // Handle edit plan
  const handleEditPlan = useCallback((plan) => {
    dispatch(openModal({
      title: "Edit Subscription Plan",
      bodyType: MODAL_BODY_TYPES.EDIT_SUBSCRIPTION_PLAN,
      size: 'lg',
      extraObject: { plan }
    }));
  }, [dispatch]);

  // Handle delete plan
  const handleDeletePlan = useCallback((plan) => {
    dispatch(openModal({
      title: "Delete Subscription Plan",
      bodyType: MODAL_BODY_TYPES.DELETE_SUBSCRIPTION_PLAN_CONFIRMATION,
      size: 'md',
      extraObject: { plan }
    }));
  }, [dispatch]);

  // Handle create plan
  const handleCreatePlan = useCallback(() => {
    dispatch(openModal({
      title: "Create New Subscription Plan",
      bodyType: MODAL_BODY_TYPES.CREATE_SUBSCRIPTION_PLAN,
      size: 'lg',
      extraObject: {}
    }));
  }, [dispatch]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'AUD'
    }).format(price);
  };

  const formatDuration = (days) => {
    if (days >= 365) {
      const years = Math.floor(days / 365);
      return `${years} year${years > 1 ? 's' : ''}`;
    } else if (days >= 30) {
      const months = Math.floor(days / 30);
      return `${months} month${months > 1 ? 's' : ''}`;
    } else {
      return `${days} day${days > 1 ? 's' : ''}`;
    }
  };

  if (isLoading && subscriptionPlans.length === 0) {
    return (
      <div className="min-h-screen bg-base-200 py-8">
        <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-3">
          <div className="flex items-center justify-center h-64">
            <div className="loading loading-spinner loading-lg text-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 py-8">
      <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-3">
        {/* Header */}
        <div className="bg-base-100 rounded-xl shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-bold text-base-content mb-2">Subscription Plans</h1>
              <p className="text-base-content/70">Manage your subscription plans and pricing</p>
            </div>
            <button 
              className="btn btn-primary text-base-100"
              onClick={handleCreatePlan}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create Plan
            </button>
          </div>
          
          {/* Search Input */}
          <div className="flex flex-col lg:flex-row gap-4 min-w-0">
            <SearchInput
              value={localSearchTerm}
              onChange={handleSearch}
              onFocus={handleSearchInputFocus}
              onBlur={handleSearchInputBlur}
              inputRef={searchInputRef}
              placeholder="Search subscription plans..."
            />
          </div>
        </div>


        {/* Subscription Plans Grid */}
        {subscriptionPlans.length === 0 && !isLoading ? (
          <div className="bg-base-100 rounded-xl shadow-sm p-12 text-center">
            <div className="text-base-content/50 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-base-content mb-2">No Subscription Plans Found</h3>
            <p className="text-base-content/70 mb-6">Get started by creating your first subscription plan.</p>
            <button 
              className="btn btn-primary text-base-100"
              onClick={handleCreatePlan}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create First Plan
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {subscriptionPlans.map((plan) => (
              <div key={plan._id} className="bg-base-100 rounded-xl border border-primary/30 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col min-w-0 overflow-hidden">
                <div className="p-6 flex flex-col flex-1">
                  {/* Plan Header */}
                  <div className="flex items-start justify-between mb-4 min-w-0 gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-base-content mb-1">{plan.name}</h3>
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className="text-2xl font-bold text-primary">{formatPrice(plan.price)}</span>
                        <span className="text-sm text-base-content/70">/ {formatDuration(plan.durationDays)}</span>
                      </div>
                    </div>
                    <div className="flex flex-row flex-nowrap items-center gap-1.5 min-w-0 flex-shrink-0 max-w-[45%]">
                      <div className={`badge text-white font-medium px-1.5 py-0.5 text-[10px] sm:text-xs flex-shrink ${plan.isActive ? 'badge-success' : 'badge-error'}`}>
                        <span className="font-normal whitespace-nowrap truncate max-w-[60px]">{plan.isActive ? 'Active' : 'Inactive'}</span>
                      </div>
                      {plan.cpdAccess && (
                        <div className="badge badge-info text-white font-medium px-1.5 py-0.5 text-[10px] sm:text-xs flex-shrink">
                          <span className="font-normal whitespace-nowrap truncate max-w-[90px]">CPD Access</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Plan Description */}
                  <p className="text-base-content/70 mb-4 line-clamp-3 flex-1">{plan.description}</p>

                  {/* Plan Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-base-content/70">Duration:</span>
                      <span className="text-base-content">{formatDuration(plan.durationDays)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-base-content/70">CPD Access:</span>
                      <span className="text-base-content">{plan.cpdAccess ? 'Yes' : 'No'}</span>
                    </div>
                    {plan.stripePriceId && (
                      <div className="flex justify-between text-sm">
                        <span className="text-base-content/70">Stripe ID:</span>
                        <span className="text-base-content font-mono text-xs">{plan.stripePriceId}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-auto">
                    <button
                      className="btn btn-sm btn-outline btn-primary flex-1"
                      onClick={() => handleViewDetails(plan)}
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </button>
                    <button
                      className="btn btn-sm btn-outline btn-secondary flex-1"
                      onClick={() => handleEditPlan(plan)}
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline btn-error"
                      onClick={() => handleDeletePlan(plan)}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Component */}
        {subscriptionPlans.length > 0 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
            showPageSizeSelector={true}
            showItemCount={true}
            className="mt-6"
          />
        )}
      </div>
    </div>
  );
}

export default SubscriptionPlans;
